import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from './icons/LucideIcons';
import { toDateKey, isClosedOn, isBlackedOut } from '../utils/deliveryDays';
import { LINK_INK } from './orderListFormat';

/**
 * Month calendar that opens under the date cards (Fable), in place of the
 * browser's native date picker — which ignores the theme and knows nothing about
 * the shop's hours.
 *
 * Closed days and blackout dates are marked but stay selectable, matching the
 * native picker this replaces: staff sometimes know something the settings do
 * not. Past days are only selectable with `allowPast` (house accounts, which
 * can be billed for past service).
 *
 * Keyboard: arrows move a day / a week, Home/End jump to the week's ends,
 * PageUp/PageDown change month, Enter/Space picks, Escape closes.
 * onClose({ returnFocus }) asks the opener to take focus back after a pick or
 * Escape; a click outside leaves focus wherever the user clicked.
 */

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const fromKey = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

const blackoutReason = (date, blackoutDates) => {
  const key = toDateKey(date);
  const hit = blackoutDates?.find((b) => (typeof b === 'string' ? b : b?.date) === key);
  return (hit && typeof hit === 'object' && hit.reason) || 'Blackout date';
};

const DateCalendarPopover = ({
  value,
  onChange,
  onClose,
  storeHours,
  blackoutDates = [],
  allowPast = false,
  align = 'right', // which edge of the opener the panel lines up with
}) => {
  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);
  const todayKey = toDateKey(today);
  const initial = value ? fromKey(value) : today;

  const [month, setMonth] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const [focusKey, setFocusKey] = useState(toDateKey(initial));
  const panelRef = useRef(null);
  const gridRef = useRef(null);

  // Six rows starting on the Sunday on/before the 1st, so the panel never
  // changes height between months.
  const cells = useMemo(() => {
    const start = addDays(month, -month.getDay());
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [month]);

  const isDisabled = (date) => !allowPast && toDateKey(date) < todayKey;

  // Keep the roving focus on the focused day as it moves.
  useEffect(() => {
    const btn = gridRef.current?.querySelector(`[data-key="${focusKey}"]`);
    btn?.focus();
  }, [focusKey, month]);

  // Close on a click outside the panel. The opener toggles itself, so a click
  // on it is left to the opener rather than closing and immediately reopening.
  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest?.('[data-calendar-opener]')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  const moveFocus = (date) => {
    if (date.getMonth() !== month.getMonth() || date.getFullYear() !== month.getFullYear()) {
      setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }
    setFocusKey(toDateKey(date));
  };

  const shiftMonth = (n) => {
    const next = new Date(month.getFullYear(), month.getMonth() + n, 1);
    setMonth(next);
    const focused = fromKey(focusKey);
    const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
    setFocusKey(toDateKey(new Date(next.getFullYear(), next.getMonth(), Math.min(focused.getDate(), lastDay))));
  };

  const pick = (date) => {
    if (isDisabled(date)) return;
    onChange(toDateKey(date));
    onClose({ returnFocus: true });
  };

  const onKeyDown = (e) => {
    const focused = fromKey(focusKey);
    const moves = {
      ArrowLeft: () => moveFocus(addDays(focused, -1)),
      ArrowRight: () => moveFocus(addDays(focused, 1)),
      ArrowUp: () => moveFocus(addDays(focused, -7)),
      ArrowDown: () => moveFocus(addDays(focused, 7)),
      Home: () => moveFocus(addDays(focused, -focused.getDay())),
      End: () => moveFocus(addDays(focused, 6 - focused.getDay())),
      PageUp: () => shiftMonth(-1),
      PageDown: () => shiftMonth(1),
      Escape: () => onClose({ returnFocus: true }),
    };
    if (moves[e.key]) {
      e.preventDefault();
      moves[e.key]();
    }
  };

  // Don't page back into months with nothing selectable.
  const canGoBack =
    allowPast || month.getFullYear() > today.getFullYear() ||
    (month.getFullYear() === today.getFullYear() && month.getMonth() > today.getMonth());

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Choose a date"
      onKeyDown={onKeyDown}
      className={`absolute z-30 ${align === 'left' ? 'left-0' : 'right-0'} top-full mt-2 w-[20rem] max-w-[calc(100vw-2rem)] rounded-lg bg-white border border-gray-200 shadow-lg p-4`}
    >
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          disabled={!canGoBack}
          aria-label="Previous month"
          className="w-8 h-8 grid place-items-center rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900 disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon size={16} />
        </button>
        <span className="text-[15px] font-semibold text-gray-900" aria-live="polite">
          {month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          aria-label="Next month"
          className="w-8 h-8 grid place-items-center rounded-md text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 mb-1" aria-hidden="true">
        {WEEKDAYS.map((d) => (
          <span key={d} className="text-center text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 py-1">
            {d}
          </span>
        ))}
      </div>

      <div ref={gridRef} role="grid" className="grid grid-cols-7 gap-y-1">
        {cells.map((date) => {
          const key = toDateKey(date);
          const inMonth = date.getMonth() === month.getMonth();
          const selected = key === value;
          const isToday = key === todayKey;
          const disabled = isDisabled(date);
          const closed = isClosedOn(date, storeHours);
          const blackout = isBlackedOut(date, blackoutDates);
          const note = blackout ? blackoutReason(date, blackoutDates) : closed ? 'Shop closed' : null;

          return (
            <button
              key={key}
              type="button"
              role="gridcell"
              data-key={key}
              tabIndex={key === focusKey ? 0 : -1}
              aria-selected={selected}
              aria-disabled={disabled}
              aria-label={`${date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${note ? ` — ${note}` : ''}`}
              title={note || undefined}
              onClick={() => pick(date)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  pick(date);
                }
              }}
              className={`relative mx-auto w-9 h-9 grid place-items-center rounded-full text-sm tabular-nums transition-colors ${
                selected
                  ? 'bg-floral-700 text-white font-semibold'
                  : disabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : note
                  ? 'text-gray-500 line-through decoration-gray-400 hover:bg-gray-50'
                  : inMonth
                  ? 'text-gray-900 hover:bg-floral-50'
                  : 'text-gray-400 hover:bg-gray-50'
              } ${isToday && !selected ? 'ring-1 ring-inset ring-floral-700' : ''}`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500">
        <span>
          <span className="line-through decoration-gray-400">12</span> closed or blacked out
        </span>
        <button
          type="button"
          onClick={() => pick(today)}
          className={`px-2.5 py-1 rounded-md text-[13px] font-medium hover:bg-floral-50 ${LINK_INK}`}
        >
          Today
        </button>
      </div>
    </div>
  );
};

export default DateCalendarPopover;
